/**
 * MIDI input.
 *
 * Web MIDI is not available everywhere — Safari has it only behind a setting,
 * and any browser will refuse it on an insecure origin — so it is feature
 * detected and its absence is a normal state, not an error. When there is no
 * device (or no API), the computer keyboard stands in, which is enough to try
 * an idea without hardware.
 *
 * Nothing here touches the audio graph. It decodes bytes into note events and
 * hands them on; what happens to them is the store's decision.
 */

export interface MidiDevice {
  id: string;
  name: string;
  manufacturer: string;
}

export type MidiEvent =
  | { type: 'noteOn'; pitch: number; velocity: number }
  | { type: 'noteOff'; pitch: number }
  | { type: 'sustain'; down: boolean }
  /** The wheel, from -1 at the bottom of its travel through 0 at rest to +1 at the top. */
  | { type: 'pitchBend'; amount: number }
  /** The mod wheel, CC1, from 0 at rest to 1 at the top. */
  | { type: 'modWheel'; amount: number }
  | { type: 'allNotesOff' };

/** The wheel at rest, in the fourteen bits pitch bend is sent as. */
const BEND_CENTRE = 8192;

/** CC64. The pedal is down from this value up; the spec draws the line at the middle. */
const CC_SUSTAIN = 64;
const CC_MOD_WHEEL = 1;
const SUSTAIN_DOWN_FROM = 64;

export type MidiListener = (event: MidiEvent) => void;

export type MidiAvailability = 'unsupported' | 'insecure' | 'denied' | 'available';

interface MidiPortLike {
  id: string;
  name: string | null;
  manufacturer: string | null;
  onmidimessage: ((event: { data: Uint8Array }) => void) | null;
}

interface MidiAccessLike {
  inputs: Map<string, MidiPortLike>;
  onstatechange: (() => void) | null;
}

/**
 * A structural view of the parts of Web MIDI this code uses.
 *
 * It deliberately does not extend the DOM's `Navigator`: the lib types declare
 * `requestMIDIAccess` as always present, which is exactly the assumption this
 * module exists to avoid making.
 */
interface NavigatorWithMidi {
  requestMIDIAccess?: (options?: { sysex?: boolean }) => Promise<MidiAccessLike>;
}

/**
 * Reads `navigator` through `unknown`, because the DOM lib's `Navigator` and
 * this module's structural view of Web MIDI describe the same object with
 * incompatible types — and the whole point here is to treat the API as
 * possibly absent.
 */
function midiNavigator(): NavigatorWithMidi | undefined {
  return (globalThis as unknown as { navigator?: NavigatorWithMidi }).navigator;
}

export class MidiInput {
  private access: MidiAccessLike | null = null;
  private listeners = new Set<MidiListener>();
  private deviceListeners = new Set<(devices: MidiDevice[]) => void>();
  private selectedDeviceId: string | null = null;

  /** Why MIDI is or is not usable, without asking for permission. */
  static availability(): MidiAvailability {
    const nav = midiNavigator();
    if (!nav) return 'unsupported';
    if (typeof nav.requestMIDIAccess !== 'function') return 'unsupported';
    if (typeof globalThis.isSecureContext === 'boolean' && !globalThis.isSecureContext) return 'insecure';
    return 'available';
  }

  get isOpen(): boolean {
    return this.access !== null;
  }

  /**
   * Requests access to MIDI devices. Called from a user action, never on load:
   * some browsers show a permission prompt, and an unexplained prompt is worse
   * than no feature.
   */
  async open(): Promise<MidiDevice[]> {
    const nav = midiNavigator();
    if (!nav?.requestMIDIAccess) {
      throw new Error(
        'This browser does not provide Web MIDI, so hardware controllers cannot be used. ' +
          'The on-screen and computer keyboards still play instruments.',
      );
    }

    try {
      // Sysex is not requested: it triggers a stronger permission prompt and
      // nothing here needs it.
      this.access = await nav.requestMIDIAccess({ sysex: false });
    } catch (error) {
      throw new Error(
        `MIDI access was refused. ${error instanceof Error ? error.message : ''} `.trim() +
          'Allow MIDI devices in your browser’s site settings to use a controller.',
      );
    }

    this.access.onstatechange = () => {
      this.attachAll();
      for (const listener of this.deviceListeners) listener(this.devices());
    };
    this.attachAll();
    return this.devices();
  }

  devices(): MidiDevice[] {
    if (!this.access) return [];
    return [...this.access.inputs.values()].map((port) => ({
      id: port.id,
      name: port.name ?? 'MIDI input',
      manufacturer: port.manufacturer ?? '',
    }));
  }

  /** Null listens to every device, which is what most people want. */
  selectDevice(deviceId: string | null): void {
    this.selectedDeviceId = deviceId;
    this.attachAll();
  }

  private attachAll(): void {
    if (!this.access) return;
    for (const port of this.access.inputs.values()) {
      const wanted = this.selectedDeviceId === null || this.selectedDeviceId === port.id;
      port.onmidimessage = wanted ? (event) => this.handle(event.data) : null;
    }
  }

  private handle(data: Uint8Array): void {
    if (data.length < 2) return;
    const status = data[0] & 0xf0;
    const pitch = data[1] & 0x7f;
    const velocity = data.length > 2 ? data[2] & 0x7f : 0;

    if (status === 0x90 && velocity > 0) this.emit({ type: 'noteOn', pitch, velocity });
    // A note-on with zero velocity is a note-off; plenty of controllers send it.
    else if (status === 0x80 || (status === 0x90 && velocity === 0)) this.emit({ type: 'noteOff', pitch });
    else if (status === 0xb0 && (pitch === 123 || pitch === 120)) this.emit({ type: 'allNotesOff' });
    else if (status === 0xb0 && pitch === CC_SUSTAIN) this.emit({ type: 'sustain', down: velocity >= SUSTAIN_DOWN_FROM });
    else if (status === 0xb0 && pitch === CC_MOD_WHEEL) this.emit({ type: 'modWheel', amount: velocity / 127 });
    else if (status === 0xe0 && data.length > 2) {
      // Fourteen bits, least significant byte first, 8192 at rest. The travel
      // is not symmetric — 8192 steps below rest, 8191 above — and each side
      // is scaled by its own length, so the top of the wheel is +1 rather than
      // falling a step short of it.
      const raw = ((data[2] & 0x7f) << 7) | (data[1] & 0x7f);
      const offset = raw - BEND_CENTRE;
      this.emit({ type: 'pitchBend', amount: offset / (offset < 0 ? BEND_CENTRE : BEND_CENTRE - 1) });
    }
  }

  private emit(event: MidiEvent): void {
    for (const listener of this.listeners) listener(event);
  }

  subscribe(listener: MidiListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onDevicesChanged(listener: (devices: MidiDevice[]) => void): () => void {
    this.deviceListeners.add(listener);
    return () => this.deviceListeners.delete(listener);
  }

  /** Feeds a note in from somewhere other than a device (the typing keyboard). */
  inject(event: MidiEvent): void {
    this.emit(event);
  }

  close(): void {
    if (this.access) {
      for (const port of this.access.inputs.values()) port.onmidimessage = null;
      this.access.onstatechange = null;
    }
    this.access = null;
    this.listeners.clear();
    this.deviceListeners.clear();
  }
}

/**
 * The computer keyboard as a one-octave controller, in the layout every tracker
 * and DAW has used since the 1990s: the home row is the white keys, the row
 * above holds the black ones.
 */
const KEYBOARD_MAP: Record<string, number> = {
  a: 0,
  w: 1,
  s: 2,
  e: 3,
  d: 4,
  f: 5,
  t: 6,
  g: 7,
  y: 8,
  h: 9,
  u: 10,
  j: 11,
  k: 12,
  o: 13,
  l: 14,
  p: 15,
  ';': 16,
};

export function keyboardPitch(key: string, octave: number): number | null {
  const offset = KEYBOARD_MAP[key.toLowerCase()];
  if (offset === undefined) return null;
  const pitch = (octave + 1) * 12 + offset;
  return pitch >= 0 && pitch <= 127 ? pitch : null;
}

export function isKeyboardMusicKey(key: string): boolean {
  return key.toLowerCase() in KEYBOARD_MAP;
}
